/**
 * BranchingOverlay — narrative decision-point overlay (P16.2).
 *
 * Displays a branching question with labelled choice cards.
 * On selection: shows the branch outcome for 2 seconds, emits
 * overlay_answered event, then calls onBranch(branch).
 * Parent handles navigation if branch.next_scene_id is set.
 *
 * Props:
 *   marker    — { question, branches: [{label, outcome, next_scene_id?}] }
 *   sceneId   — string scene ID for event context
 *   onBranch  — (branch) => void called after outcome display
 *   onClose   — () => void called to dismiss without choosing
 */

import React, { useState } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

export default function BranchingOverlay({ marker, sceneId, onBranch, onClose }) {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showingOutcome, setShowingOutcome] = useState(false);

  const handleBranchSelect = async (branch) => {
    if (showingOutcome) return;
    setSelectedBranch(branch);
    setShowingOutcome(true);

    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'branching',
      scene_id: sceneId,
      selected_branch: branch.label,
    });

    setTimeout(() => {
      if (onBranch) onBranch(branch);
    }, 2000);
  };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={labelStyle}>Decision Point</span>
          {!showingOutcome && (
            <button onClick={onClose} style={skipBtnStyle}>
              Close
            </button>
          )}
        </div>

        <p style={questionStyle}>{marker?.question}</p>

        {showingOutcome ? (
          <div style={outcomeStyle}>
            <div style={subLabelStyle}>Outcome</div>
            <p style={{ margin: 0, color: '#e2e8f0', fontSize: 15, lineHeight: 1.6 }}>
              {selectedBranch?.outcome}
            </p>
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              Continuing…
            </div>
          </div>
        ) : (
          <div style={branchesContainerStyle}>
            {(marker?.branches || []).map((branch, idx) => (
              <button
                key={idx}
                onClick={() => handleBranchSelect(branch)}
                style={branchCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1e3a5f';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#0f172a';
                  e.currentTarget.style.borderColor = '#334155';
                }}
              >
                <span style={branchLabelStyle}>{branch.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const cardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 580,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const subLabelStyle = {
  fontSize: 10,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: '#64748b',
  marginBottom: 8,
};

const skipBtnStyle = {
  background: 'none',
  border: '1px solid #334155',
  borderRadius: 4,
  color: '#64748b',
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
};

const questionStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 20px',
  lineHeight: 1.5,
};

const branchesContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const branchCardStyle = {
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 10,
  padding: '14px 18px',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.15s, border-color 0.15s',
  width: '100%',
};

const branchLabelStyle = {
  fontSize: 15,
  color: '#e2e8f0',
  fontWeight: 500,
};

const outcomeStyle = {
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 10,
  padding: '16px 18px',
  animation: 'fadeIn 0.3s ease',
};
